import { CSSProperties, useEffect, useRef, useState } from "react";
import { Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BannerPresentation } from "@/lib/bannerPresentation";

export function bannerFocalStyle(settings: BannerPresentation): CSSProperties {
  return {
    "--banner-desktop-position": `${settings.desktop.x}% ${settings.desktop.y}%`,
    "--banner-mobile-position": `${settings.mobile.x}% ${settings.mobile.y}%`,
  } as CSSProperties;
}

export default function BannerMedia({ desktop, mobile, alt, settings, desktopVideo, mobileVideo, language = "en", previewViewport, active = true, warm = false, onReady }: {
  desktop: string; mobile: string; alt: string; settings: BannerPresentation;
  desktopVideo?: string | null; mobileVideo?: string | null; language?: "en" | "ar"; previewViewport?: "desktop" | "mobile";
  /** Active slide: video plays. Warm slide (incoming): video preloads but stays paused. */
  active?: boolean; warm?: boolean;
  /** Fires once the initially visible media (still image) is decoded, or on error. */
  onReady?: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [wide, setWide] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  const [blocked, setBlocked] = useState(false);
  const [failed, setFailed] = useState(false);
  // Mount the video once the slide has been active (or warmed) at least once,
  // then keep it mounted so revisits don't reload it.
  const [seen, setSeen] = useState(active || warm);
  const [playing, setPlaying] = useState(false);
  const readyFired = useRef(false);
  useEffect(() => {
    const media = window.matchMedia("(min-width: 1024px)");
    const change = () => setWide(media.matches);
    media.addEventListener("change", change);
    return () => media.removeEventListener("change", change);
  }, []);
  useEffect(() => {
    if (active || warm) setSeen(true);
  }, [active, warm]);
  const desktopMode = previewViewport ? previewViewport === "desktop" : wide;
  const source = seen ? desktopMode ? desktopVideo || mobileVideo : mobileVideo || desktopVideo : null;
  const still = desktopMode ? desktop || mobile : mobile || desktop;
  const fireReady = () => {
    if (!readyFired.current) {
      readyFired.current = true;
      onReady?.();
    }
  };
  // Pause when the slide is not active; resume playback when it becomes active again.
  useEffect(() => {
    const el = video.current;
    if (!el || !source || failed) return;
    if (!active) {
      el.pause();
      return;
    }
    if (el.paused) {
      void el.play().then(() => setBlocked(false)).catch(() => setBlocked(true));
    }
  }, [active, source, failed]);
  useEffect(() => {
    setBlocked(false);
    setFailed(false);
    if (!source) return;
    const timer = window.setTimeout(() => { if (active && video.current?.paused) setBlocked(true); }, 2000);
    return () => window.clearTimeout(timer);
  }, [source, active]);
  const artwork = settings.format === "artwork";
  return <div className={`banner-media ${artwork ? "banner-media-artwork" : "banner-media-photo"} ${settings.fit === "cover" && !artwork ? "banner-media-crop" : ""}`} style={bannerFocalStyle(settings)}>
    {source && !failed ? <>
      {/* The still stays mounted underneath: it sizes the container, shows while the
          video buffers, and remains as the fallback if the video cannot play. */}
      <img src={still} alt={alt} className="banner-media-still" loading="eager" decoding="async" ref={(el) => { if (el?.complete && el.naturalWidth > 0) fireReady(); }} onLoad={fireReady} onError={fireReady} />
      <video ref={video} key={source} src={source} data-playing={playing || undefined} loop muted playsInline preload="metadata" aria-label={alt}
        onCanPlay={() => { if (active) void video.current?.play().catch(() => setBlocked(true)); }}
        onPlaying={() => { setBlocked(false); setPlaying(true); }}
        onError={() => { setBlocked(true); setFailed(true); fireReady(); }} />
      {blocked && active && <Button type="button" variant="outline" size="icon" className="banner-play rounded-full border-gold bg-anaya-background text-anaya-ink" aria-label={language === "ar" ? "تشغيل الفيديو" : "Play video"}
        onClick={(event) => { event.stopPropagation(); void video.current?.play().then(() => setBlocked(false)).catch(() => setBlocked(true)); }}><Play /></Button>}
    </> : <picture>
      {!previewViewport && <source media="(min-width: 1024px)" srcSet={desktop || mobile} />}
      <img src={previewViewport ? desktopMode ? desktop || mobile : mobile || desktop : mobile || desktop} alt={alt} loading="eager" decoding="async" ref={(el) => { if (el?.complete && el.naturalWidth > 0) fireReady(); }} onLoad={fireReady} onError={fireReady} />
    </picture>}
  </div>;
}
